import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, authedFetch } from './supabaseClient';
import DashboardView from './views/DashboardView';
import UploadView from './views/UploadView';
import RevenueAnalysisView from './views/RevenueAnalysisView';
import MrrBuildView from './views/MrrBuildView';
import CohortAnalysisView from './views/CohortAnalysisView';
import CacPaybackView from './views/CacPaybackView';
import KpiChartsView from './views/KpiChartsView';
import BenchmarkingView from './views/BenchmarkingView';
import InsightsView from './views/InsightsView';
 
// ── Constants ─────────────────────────────────────────────────────────────────
 
export const DEMO_PROJECT_ID = '00000000-0000-0000-0000-000000000002';
export const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

export type UnitScale = 1 | 1000 | 1000000;
export function unitLabel(s: UnitScale): string {
  return s === 1 ? '($)' : s === 1000 ? '($000)' : '($M)';
}
 
const SECTIONS = [
  { id: 'upload',     label: 'Upload & Edit Data' },
  { id: 'kpi',        label: 'Performance Trends' },
  { id: 'dashboard',  label: 'Dashboard' },
  { id: 'revenue',    label: 'Customer Revenue' },
  { id: 'cohorts',    label: 'Cohort Analysis' },
  { id: 'mrr',        label: 'Revenue Build' },
  { id: 'cac',        label: 'CAC & LTV' },
  { id: 'benchmarks', label: 'Benchmarking' },
  { id: 'insights',   label: 'Insights' },
];
 
// ── Types ─────────────────────────────────────────────────────────────────────
 
export interface Project {
  id: string;
  name: string;
  created_at: string;
  financials_period_type: string | null;
  customers_period_type: string | null;
  stage: string | null;
  sector: string | null;
  business_model: string | null;
  first_revenue_period: string | null;
  pic: string | null;
  include_in_benchmarks: boolean;
}
 
// ── useResponsive Hook ───────────────────────────────────────────────────────
// Returns two values:
//   isMobile       — viewport ≤ 1023px. Controls sidebar/hamburger toggle.
//   isPhoneDevice  — portrait phone (≤ 767px) OR landscape phone (≤ 1023px
//                    AND ≤ 500px tall). Controls upload/edit restriction and
//                    account deletion hiding. Does NOT trigger on tablets or
//                    small laptop windows.

function useResponsive() {
  const compute = () => {
    if (typeof window === 'undefined') return { isMobile: false, isPhoneDevice: false };
    const w = window.innerWidth;
    const h = window.innerHeight;
    return {
      isMobile: w <= 1023,
      isPhoneDevice: w <= 767 || (w <= 1023 && h <= 500),
    };
  };

  const [state, setState] = useState(compute);

  useEffect(() => {
    const handler = () => setState(compute());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return state;
}

// ── Auth Context ──────────────────────────────────────────────────────────────
 
interface AuthContextType {
  isAuthenticated: boolean;
  userId: string | null;
  userEmail: string | null;
  projectId: string;
  activeProject: Project | null;
  projects: Project[];
  setActiveProject: (p: Project) => void;
  refreshProjects: (currentUser?: any, preserveActive?: boolean) => Promise<void>;
  login: (email: string, password: string) => Promise<string | null>;
  signup: (email: string, password: string) => Promise<string | null>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<string | null>;
  updatePassword: (newPassword: string) => Promise<string | null>;
  deleteAccount: () => Promise<string | null>;
  deleteProject: (projectId: string) => Promise<string | null>;
}
 
const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  userId: null,
  userEmail: null,
  projectId: DEMO_PROJECT_ID,
  activeProject: null,
  projects: [],
  setActiveProject: () => {},
  refreshProjects: async () => {},
  login: async () => null,
  signup: async () => null,
  logout: async () => {},
  resetPassword: async () => null,
  updatePassword: async () => null,
  deleteAccount: async () => null,
  deleteProject: async () => null,
});
 
export const useAuth = () => useContext(AuthContext);
 
// ── API Helper ────────────────────────────────────────────────────────────────
 
export function apiUrl(path: string, projectId: string, extra?: Record<string, string>): string {
  const params = new URLSearchParams({ project_id: projectId, ...extra });
  return `${API_BASE}${path}?${params.toString()}`;
}
 
// ── Auth Provider ─────────────────────────────────────────────────────────────
 
function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser]                     = useState<User | null>(null);
  const [projects, setProjects]             = useState<Project[]>([]);
  const [activeProject, setActiveProjectState] = useState<Project | null>(null);
  const [authReady, setAuthReady]           = useState(false);
 
  const isAuthenticated = !!user;
  const userId          = user?.id ?? null;
  const userEmail       = user?.email ?? null;
  const projectId       = activeProject?.id ?? DEMO_PROJECT_ID;
 
  // ── Fetch user's projects, default to most recent ────────────────────────
 
  const refreshProjects = async (currentUser?: User | null, preserveActive = false) => {
    const u = currentUser ?? user;
    if (!u) return;
    const { data, error } = await supabase
      .from('projects')
      .select('id, name, created_at, financials_period_type, customers_period_type, stage, sector, business_model, first_revenue_period, pic, include_in_benchmarks')
      .eq('user_id', u.id)
      .eq('is_demo', false)
      .order('created_at', { ascending: false });
 
    if (!error && data) {
      setProjects(data);
      if (!preserveActive) {
        // On login/session restore: default to most recent, but only if
        // no project is already active (don't override a user's current selection)
        setActiveProjectState(prev => prev ?? (data.length > 0 ? data[0] : null));
      } else {
        // preserveActive=true: keep the user on the same project they're on,
        // but refresh that project's field values from the new data. Without
        // this, saves to project metadata (Stage, Sector, Business Model,
        // First Revenue Period, PIC) would update `projects[]` but leave
        // `activeProject` holding the stale pre-save snapshot — causing
        // UploadView to render blank fields on the next tab switch even
        // though the values are correctly persisted in the DB and visible
        // elsewhere in the app (e.g., Benchmarking, which re-fetches).
        // If the active project no longer exists in the refreshed list
        // (e.g., deleted in another tab), preserve the prev value — the
        // delete flow handles reassignment separately.
        setActiveProjectState(prev => {
          if (!prev) return prev;
          const refreshed = data.find(p => p.id === prev.id);
          return refreshed ?? prev;
        });
      }
    }
  };
 
  // ── Session persistence — restore on reload ──────────────────────────────
 
  useEffect(() => {
    // Handle password reset links — detect recovery token in URL
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const type = url.searchParams.get('type');
 
    if (code && type === 'recovery') {
      // Exchange code for session — handled in PasswordResetHandler component
      setAuthReady(true);
      return;
    }
 
    // Restore existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        await refreshProjects(session.user);
      }
      setAuthReady(true);
    });
 
    // Listen for auth state changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user ?? null;
      setUser(u);
      if (u) {
        refreshProjects(u);
      } else {
        setProjects([]);
        setActiveProjectState(null);
      }
    });
 
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
 
  // ── Auth actions ─────────────────────────────────────────────────────────
 
  const login = async (email: string, password: string): Promise<string | null> => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return error ? error.message : null;
  };
 
  const signup = async (email: string, password: string): Promise<string | null> => {
    const { error } = await supabase.auth.signUp({ email, password });
    return error ? error.message : null;
  };
 
  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProjects([]);
    setActiveProjectState(null);
  };
 
  const resetPassword = async (email: string): Promise<string | null> => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}?type=recovery`,
    });
    return error ? error.message : null;
  };
 
  const updatePassword = async (newPassword: string): Promise<string | null> => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return error ? error.message : null;
  };
 
  const deleteAccount = async (): Promise<string | null> => {
    if (!userId) return 'Not logged in';
    try {
      // Delete all user projects and their data (cascade handles financials/customers)
      await supabase.from('projects').delete().eq('user_id', userId);
      // Delete auth user via RPC (requires delete_user_account function in Supabase)
      const { error } = await supabase.rpc('delete_user_account', { user_uuid: userId });
      if (error) throw error;
      await logout();
      return null;
    } catch (e: any) {
      return e.message ?? 'Failed to delete account';
    }
  };

  const deleteProject = async (pid: string): Promise<string | null> => {
    try {
      const { error } = await supabase.from('projects').delete().eq('id', pid);
      if (error) throw error;
      // Refresh projects list and switch to most recent remaining, or clear if none
      const { data } = await supabase
        .from('projects')
        .select('id, name, created_at, financials_period_type, customers_period_type, stage, sector, business_model, first_revenue_period, pic, include_in_benchmarks')
        .eq('user_id', userId!)
        .eq('is_demo', false)
        .order('created_at', { ascending: false });
      const remaining = data ?? [];
      setProjects(remaining);
      setActiveProjectState(remaining.length > 0 ? remaining[0] : null);
      return null;
    } catch (e: any) {
      return e.message ?? 'Failed to delete project';
    }
  };
 
  const setActiveProject = (p: Project) => setActiveProjectState(p);
 
  if (!authReady) return null; // Wait for session restore before rendering
 
  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      userId,
      userEmail,
      projectId,
      activeProject,
      projects,
      setActiveProject,
      refreshProjects,
      login,
      signup,
      logout,
      resetPassword,
      updatePassword,
      deleteAccount,
      deleteProject,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
 
// ── Password Reset Handler ────────────────────────────────────────────────────
// Shown when user arrives via a password reset email link.
// Exchanges the code, then shows a new password form.
 
function PasswordResetHandler({ onDone }: { onDone: () => void }) {
  const { updatePassword } = useAuth();
  const [stage, setStage]   = useState<'exchanging' | 'form' | 'success' | 'error'>('exchanging');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [error, setError]       = useState('');
 
  useEffect(() => {
    const url = new URL(window.location.href);
    const token_hash = url.searchParams.get('token_hash');
    const type = url.searchParams.get('type');

    if (!token_hash || type !== 'recovery') { setStage('error'); return; }

    supabase.auth.verifyOtp({ token_hash, type: 'recovery' }).then(({ error }) => {
      if (error) { setError(error.message); setStage('error'); }
      else setStage('form');
    });
  }, []);
 
  const handleSubmit = async () => {
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 8)  { setError('Password must be at least 8 characters'); return; }
    setError('');
    const err = await updatePassword(password);
    if (err) { setError(err); return; }
    setStage('success');
    // Clear recovery params from URL and return to app after 2s
    setTimeout(() => {
      window.history.replaceState({}, '', '/');
      onDone();
    }, 2000);
  };
 
  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 w-full max-w-sm space-y-5">
        <div>
          <h1 className="text-white font-semibold text-base">Reset Your Password</h1>
          <p className="text-gray-500 text-xs mt-1">MetricFrame</p>
        </div>
 
        {stage === 'exchanging' && (
          <p className="text-gray-400 text-sm">Verifying your reset link...</p>
        )}
 
        {stage === 'error' && (
          <div className="space-y-3">
            <p className="text-red-400 text-sm">This reset link is invalid or has expired.</p>
            <p className="text-gray-500 text-xs">Please request a new password reset from the login screen.</p>
          </div>
        )}
 
        {stage === 'form' && (
          <div className="space-y-3">
            <input
              type="password"
              placeholder="New password (min 8 characters)"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
            />
            {error && <p className="text-red-400 text-xs">{error}</p>}
            <button
              onClick={handleSubmit}
              className="w-full bg-blue-600 hover:bg-blue-500 text-white text-sm py-2 rounded transition-colors font-medium"
            >
              Update Password
            </button>
          </div>
        )}
 
        {stage === 'success' && (
          <div className="text-center space-y-2">
            <p className="text-emerald-400 text-sm font-medium">✓ Password updated!</p>
            <p className="text-gray-500 text-xs">Returning to MetricFrame...</p>
          </div>
        )}
      </div>
    </div>
  );
}
 
// ── App Root ──────────────────────────────────────────────────────────────────
 
export default function App() {
  const [isReset, setIsReset] = useState(() => {
    const url = new URL(window.location.href);
    return url.searchParams.get('type') === 'recovery';
  });
 
  if (isReset) {
    return (
      <AuthProvider>
        <PasswordResetHandler onDone={() => setIsReset(false)} />
      </AuthProvider>
    );
  }
 
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}
 
// ── Sidebar Content ──────────────────────────────────────────────────────────
// Extracted so both the desktop aside and mobile drawer render the same content.

function SidebarContent({
  activeSection,
  onSectionChange,
  isAuthenticated,
  userEmail,
  onShowAccount,
  onLogout,
  onAuthPanel,
  onLogoClick,
}: {
  activeSection: string;
  onSectionChange: (id: string) => void;
  isAuthenticated: boolean;
  userEmail: string | null;
  onShowAccount: () => void;
  onLogout: () => void;
  onAuthPanel: (mode: 'login' | 'signup') => void;
  onLogoClick?: () => void;
}) {
  return (
    <>
      <div
        className={`px-5 py-5 border-b border-gray-800${onLogoClick ? ' cursor-pointer hover:bg-gray-800 transition-colors' : ''}`}
        onClick={onLogoClick}
      >
        <img
          src="/metricframe-logo.png"
          alt="MetricFrame"
          className="h-7"
        />
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {SECTIONS.map(s => (
          <button
            key={s.id}
            onClick={() => onSectionChange(s.id)}
            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors min-h-[44px] flex items-center ${
              activeSection === s.id
                ? 'bg-blue-600 text-white font-medium'
                : 'text-gray-400 hover:bg-gray-800 hover:text-white'
            }`}
          >
            {s.label}
          </button>
        ))}
      </nav>

      {/* Sidebar footer — auth state */}
      <div className="px-4 py-4 border-t border-gray-800">
        {isAuthenticated ? (
          <div className="space-y-2">
            <p className="text-xs text-gray-400 truncate">{userEmail}</p>
            <div className="flex gap-3">
              <button
                onClick={onShowAccount}
                className="text-xs text-gray-500 hover:text-white transition-colors min-h-[44px] lg:min-h-0"
              >
                Account
              </button>
              <span className="text-gray-700 text-xs flex items-center">·</span>
              <button
                onClick={onLogout}
                className="text-xs text-gray-500 hover:text-white transition-colors min-h-[44px] lg:min-h-0"
              >
                Log out
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-gray-400 font-medium">Exploring sample data</p>
            <p className="text-xs text-gray-600">Sign up to save your own data</p>
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => onAuthPanel('login')}
                className="text-xs text-gray-400 hover:text-white transition-colors min-h-[44px] lg:min-h-0"
              >
                Log in
              </button>
              <span className="text-gray-700 text-xs flex items-center">·</span>
              <button
                onClick={() => onAuthPanel('signup')}
                className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors min-h-[44px] lg:min-h-0"
              >
                Sign up
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ── App Shell ─────────────────────────────────────────────────────────────────
 
function AppShell() {
  const {
    isAuthenticated, userEmail, userId, projectId,
    activeProject, projects, setActiveProject,
    logout,
  } = useAuth();

  const { isMobile, isPhoneDevice } = useResponsive();
 
  const [activeSection, setActiveSection] = useState('dashboard');
  const [authPanel, setAuthPanel]         = useState<'none' | 'login' | 'signup' | 'forgot'>('none');
  const [showAccount, setShowAccount]     = useState(false);
  const [drawerOpen, setDrawerOpen]       = useState(false);
  const [sidebarOpen, setSidebarOpen]     = useState(true);

  // Shared customer analysis filters — persists across Revenue, MRR, and Cohort tabs
  const [customerFilters, setCustomerFilters] = useState<Record<string, string>>({});

  // Dollar display unit — shared across all tabs
  const [unitScale, setUnitScale] = useState<UnitScale>(1000);

  // Export loading state
  const [exporting, setExporting] = useState(false);

  // Close drawer when switching to desktop
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  // Section change — also closes drawer on mobile
  const handleSectionChange = useCallback((id: string) => {
    setActiveSection(id);
    if (isMobile) setDrawerOpen(false);
  }, [isMobile]);

  // Auth panel opener — also closes drawer on mobile
  const handleAuthPanel = useCallback((mode: 'login' | 'signup') => {
    setAuthPanel(mode);
    if (isMobile) setDrawerOpen(false);
  }, [isMobile]);

  // Account opener — also closes drawer on mobile
  const handleShowAccount = useCallback(() => {
    setShowAccount(true);
    if (isMobile) setDrawerOpen(false);
  }, [isMobile]);

  // Logout — also closes drawer on mobile
  const handleLogout = useCallback(() => {
    logout();
    if (isMobile) setDrawerOpen(false);
  }, [isMobile, logout]);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = new URLSearchParams({
        project_id: projectId,
        unit_scale: String(unitScale),
      });
      if (userId) params.set('user_id', userId);
      // Pass active customer filters
      for (const [k, v] of Object.entries(customerFilters)) {
        if (v) params.set(k, v);
      }
      const resp = await authedFetch(`${API_BASE}/api/export/full?${params.toString()}`);
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Extract filename from Content-Disposition header, or use fallback
      const cd = resp.headers.get('Content-Disposition');
      const match = cd?.match(/filename="?([^"]+)"?/);
      a.download = match?.[1] || 'MetricFrame_Live_Model.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export error:', e);
    } finally {
      setExporting(false);
    }
  };
 
  return (
    <div
      className="flex h-screen bg-gray-950 text-gray-100 font-sans"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
      }}
    >
 
      {/* ── Desktop sidebar ── */}
      {!isMobile && sidebarOpen && (
        <aside className="w-56 bg-gray-900 border-r border-gray-800 flex flex-col flex-shrink-0">
          <SidebarContent
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
            isAuthenticated={isAuthenticated}
            userEmail={userEmail}
            onShowAccount={handleShowAccount}
            onLogout={handleLogout}
            onAuthPanel={handleAuthPanel}
            onLogoClick={() => setSidebarOpen(false)}
          />
        </aside>
      )}

      {/* ── Mobile drawer overlay ── */}
      {isMobile && drawerOpen && (
        <div
          className="fixed inset-0 z-40 flex"
          style={{ paddingLeft: 'env(safe-area-inset-left, 0px)' }}
        >
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60"
            onClick={() => setDrawerOpen(false)}
          />
          {/* Drawer panel */}
          <div className="relative w-64 bg-gray-900 border-r border-gray-800 flex flex-col z-50 overflow-y-auto">
            <SidebarContent
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
              isAuthenticated={isAuthenticated}
              userEmail={userEmail}
              onShowAccount={handleShowAccount}
              onLogout={handleLogout}
              onAuthPanel={handleAuthPanel}
            />
          </div>
        </div>
      )}
 
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
 
        {/* Top bar */}
        <header className="h-14 bg-gray-900 border-b border-gray-800 flex items-center px-4 md:px-6 justify-between flex-shrink-0">

          {/* Left side */}
          <div className="flex items-center gap-3">
            {/* Hamburger — visible on mobile always, or desktop when sidebar collapsed */}
            {(isMobile || !sidebarOpen) && (
              <button
                className="p-2 -ml-2 text-gray-400 hover:text-white transition-colors"
                onClick={() => isMobile ? setDrawerOpen(true) : setSidebarOpen(true)}
                aria-label="Open menu"
              >
                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
              </button>
            )}

            {/* Section label — visible when sidebar is open on desktop */}
            {!isMobile && sidebarOpen && (
              <h1 className="text-sm font-medium text-gray-300">
                {SECTIONS.find(s => s.id === activeSection)?.label}
              </h1>
            )}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2 md:gap-3 text-xs">
            {/* Active project name or demo badge */}
            {isAuthenticated && activeProject ? (
              <div className="relative">
                <select
                  value={activeProject.id}
                  onChange={e => {
                    const p = projects.find(p => p.id === e.target.value);
                    if (p) setActiveProject(p);
                  }}
                  className="bg-gray-800 border border-gray-700 rounded px-2 md:px-3 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-blue-500 cursor-pointer max-w-[140px] md:max-w-none truncate"
                >
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            ) : isAuthenticated && projects.length === 0 ? (
              <span className="px-2 py-1 bg-gray-800 rounded text-gray-400">No projects yet — sample data</span>
            ) : (
              <span className="px-2 py-1 bg-gray-800 border border-gray-700 rounded text-gray-400">
                Sample data
              </span>
            )}
            {/* Period type badge — desktop only */}
            {activeProject?.financials_period_type && (
              <span className="hidden lg:inline-block px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-gray-400 capitalize">
                {activeProject.financials_period_type}
              </span>
            )}
            {/* Unit scale selector */}
            <select
              value={unitScale}
              onChange={e => setUnitScale(Number(e.target.value) as UnitScale)}
              className="bg-gray-800 border border-gray-700 rounded px-2 md:px-3 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-blue-500 cursor-pointer"
            >
              <option value={1}>$</option>
              <option value={1000}>$000</option>
              <option value={1000000}>$M</option>
            </select>
            {/* Live Model export button */}
            <div className="relative group">
              <button
                onClick={handleExport}
                disabled={exporting}
                className="p-1.5 rounded border border-gray-700 bg-gray-800 text-gray-400 hover:text-white hover:border-gray-600 transition-colors disabled:opacity-40 disabled:cursor-wait"
                aria-label="Export Live Model"
              >
                {exporting ? (
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" strokeDasharray="56" strokeDashoffset="14" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                )}
              </button>
              {/* Export tooltip — hidden on mobile (hover doesn't work on touch) */}
              <div className="hidden lg:block absolute right-0 top-full mt-2 w-64 opacity-0 group-hover:opacity-100 transition-opacity duration-150 delay-300 pointer-events-none z-50">
                <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-xl px-4 py-3 text-left">
                  <p className="text-xs font-bold text-white mb-1">Export Live Model</p>
                  <p className="text-[11px] text-gray-500 leading-relaxed">
                    Download the formula-linked Excel model: every value is an
                    auditable formula tracing back to your source data. Includes the
                    Executive Summary, P&amp;L, Operating Metrics, Revenue Build,
                    Balance Sheet &amp; Cash Flow, CAC &amp; LTV, Cohort Analysis,
                    and Benchmarking. Respects your current display units and any
                    active filters.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </header>
 
        {/* Auth panel */}
        {authPanel !== 'none' && (
          <AuthPanel
            mode={authPanel}
            onClose={() => setAuthPanel('none')}
            onSwitch={setAuthPanel}
          />
        )}
 
        {/* Account panel */}
        {showAccount && (
          <AccountPanel onClose={() => setShowAccount(false)} isPhoneDevice={isPhoneDevice} />
        )}
 
        {/* Page content */}
        <main className={`flex-1 ${activeSection === 'benchmarks' ? 'overflow-hidden' : 'overflow-auto p-4 md:p-6'}`}>
          {activeSection === 'dashboard' && (
            <DashboardView projectId={projectId} unitScale={unitScale} />
          )}
          {activeSection === 'upload' && (
            isPhoneDevice ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center space-y-2 px-6">
                  <p className="text-gray-400 text-sm">Editing is available on desktop.</p>
                  <p className="text-gray-500 text-xs">Upload files, manage projects, and edit categorizations from a desktop browser.</p>
                </div>
              </div>
            ) : (
              <UploadView
                projectId={projectId}
                isAuthenticated={isAuthenticated}
              />
            )
          )}
          {activeSection === 'revenue' && (
            <RevenueAnalysisView
              projectId={projectId}
              filters={customerFilters}
              onFiltersChange={setCustomerFilters}
              unitScale={unitScale}
            />
          )}
          {activeSection === 'mrr' && (
            <MrrBuildView
              projectId={projectId}
              filters={customerFilters}
              onFiltersChange={setCustomerFilters}
              unitScale={unitScale}
            />
          )}
          {activeSection === 'cohorts' && (
            <CohortAnalysisView
              projectId={projectId}
              filters={customerFilters}
              onFiltersChange={setCustomerFilters}
              unitScale={unitScale}
            />
          )}
          {activeSection === 'cac' && (
            <CacPaybackView projectId={projectId} unitScale={unitScale} />
          )}
          {activeSection === 'kpi' && (
            <KpiChartsView projectId={projectId} unitScale={unitScale} />
          )}
          {activeSection === 'benchmarks' && (
            <BenchmarkingView unitScale={unitScale} />
          )}
          {activeSection === 'insights' && (
            <InsightsView projectId={projectId} isAuthenticated={isAuthenticated} unitScale={unitScale} />
          )}
          {activeSection !== 'dashboard' && activeSection !== 'upload' && activeSection !== 'revenue' && activeSection !== 'mrr' && activeSection !== 'cohorts' && activeSection !== 'cac' && activeSection !== 'kpi' && activeSection !== 'benchmarks' && activeSection !== 'insights' && (
                      <div className="flex items-center justify-center h-64">
              <p className="text-gray-600 text-sm">Coming soon</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
 
// ── Auth Panel ────────────────────────────────────────────────────────────────
 
type AuthMode = 'login' | 'signup' | 'forgot' | 'none';
 
function AuthPanel({
  mode,
  onClose,
  onSwitch,
}: {
  mode: AuthMode;
  onClose: () => void;
  onSwitch: (m: AuthMode) => void;
}) {
  const { login, signup, resetPassword } = useAuth();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [message, setMessage]   = useState('');
  const [loading, setLoading]   = useState(false);
 
  const handleSubmit = async () => {
    setError(''); setMessage(''); setLoading(true);
 
    if (mode === 'login') {
      const err = await login(email, password);
      if (err) setError(err);
      else onClose();
 
    } else if (mode === 'signup') {
      if (password.length < 8) { setError('Password must be at least 8 characters'); setLoading(false); return; }
      const err = await signup(email, password);
      if (err) {
        if (err.toLowerCase().includes('invite_only') || err.toLowerCase().includes('hook')) {
          setError('MetricFrame is currently invite-only. Request access at contact@metricfra.me');
        } else {
          setError(err);
        }
      } else {
        setMessage('Check your email for a verification link.');
      }
 
    } else if (mode === 'forgot') {
      const err = await resetPassword(email);
      if (err) setError(err);
      else setMessage('Reset link sent — check your email.');
    }
 
    setLoading(false);
  };
 
  const titles: Record<string, string> = {
    login: 'Log In', signup: 'Create Account', forgot: 'Reset Password',
  };
 
  return (
    <div className="border-b border-gray-800 bg-gray-900/95 px-4 md:px-6 py-5 flex-shrink-0">
      <div className="max-w-[26rem] space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white">{titles[mode]}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xs transition-colors p-2 -mr-2">✕</button>
        </div>
 
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
        />
 
        {mode !== 'forgot' && (
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
        )}
 
        {error   && <p className="text-red-400 text-xs">{error}</p>}
        {message && <p className="text-emerald-400 text-xs">{message}</p>}
 
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm py-2 rounded transition-colors font-medium"
        >
          {loading ? 'Please wait...' : titles[mode]}
        </button>
 
        {/* Mode switcher links */}
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs pt-1">
          {mode === 'login' && (
            <>
              <button onClick={() => onSwitch('signup')} className="text-gray-500 hover:text-white transition-colors">
                Create account
              </button>
              <span className="text-gray-700">·</span>
              <button onClick={() => onSwitch('forgot')} className="text-gray-500 hover:text-white transition-colors">
                Forgot password?
              </button>
            </>
          )}
          {mode === 'signup' && (
            <>
              <button onClick={() => onSwitch('login')} className="text-gray-500 hover:text-white transition-colors">
                Already have an account? Log in
              </button>
              <span className="text-gray-700">·</span>
              <span className="text-gray-500">Request Access: <a href="mailto:contact@metricfra.me?subject=MetricFrame%20Access%20Request" className="hover:text-gray-400 underline">contact@metricfra.me</a></span>
            </>
          )}
          {mode === 'forgot' && (
            <button onClick={() => onSwitch('login')} className="text-gray-500 hover:text-white transition-colors">
              ← Back to log in
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
 
// ── Account Panel ─────────────────────────────────────────────────────────────
 
function AccountPanel({ onClose, isPhoneDevice }: { onClose: () => void; isPhoneDevice: boolean }) {
  const { userEmail, updatePassword, deleteAccount } = useAuth();
  const [newPw, setNewPw]         = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [pwError, setPwError]     = useState('');
  const [pwMessage, setPwMessage] = useState('');
  const [delError, setDelError]   = useState('');
  const [loading, setLoading]     = useState(false);
 
  const handlePasswordUpdate = async () => {
    setPwError(''); setPwMessage('');
    if (newPw !== confirmPw) { setPwError('Passwords do not match'); return; }
    if (newPw.length < 8)    { setPwError('Password must be at least 8 characters'); return; }
    setLoading(true);
    const err = await updatePassword(newPw);
    setLoading(false);
    if (err) setPwError(err);
    else { setPwMessage('Password updated successfully.'); setNewPw(''); setConfirmPw(''); }
  };
 
  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') { setDelError("Type DELETE to confirm"); return; }
    setLoading(true);
    const err = await deleteAccount();
    setLoading(false);
    if (err) setDelError(err);
    // On success, auth state change will log out automatically
  };
 
  return (
    <div className="border-b border-gray-800 bg-gray-900/95 px-4 md:px-6 py-5 flex-shrink-0">
      <div className="max-w-sm space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white">Account</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xs transition-colors p-2 -mr-2">✕</button>
        </div>
 
        <p className="text-xs text-gray-400">{userEmail}</p>
 
        {/* Change password */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-300">Change Password</p>
          <input
            type="password"
            placeholder="New password"
            value={newPw}
            onChange={e => setNewPw(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
          <input
            type="password"
            placeholder="Confirm new password"
            value={confirmPw}
            onChange={e => setConfirmPw(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
          {pwError   && <p className="text-red-400 text-xs">{pwError}</p>}
          {pwMessage && <p className="text-emerald-400 text-xs">{pwMessage}</p>}
          <button
            onClick={handlePasswordUpdate}
            disabled={loading}
            className="bg-gray-700 hover:bg-gray-600 text-white text-xs px-4 py-2 rounded transition-colors"
          >
            Update Password
          </button>
        </div>
 
        {/* Danger zone — hidden on phone devices */}
        {isPhoneDevice ? (
          <p className="text-xs text-gray-600">To delete your account, open MetricFrame on a desktop browser.</p>
        ) : (
          <div className="border border-red-900/40 rounded-lg p-4 space-y-2">
            <p className="text-xs font-medium text-red-400">Danger Zone</p>
            <p className="text-xs text-gray-500">
              Permanently delete your account and all project data. This cannot be undone.
            </p>
            <input
              type="text"
              placeholder='Type DELETE to confirm'
              value={deleteConfirm}
              onChange={e => setDeleteConfirm(e.target.value)}
              className="w-full bg-gray-800 border border-red-900/40 rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-red-500"
            />
            {delError && <p className="text-red-400 text-xs">{delError}</p>}
            <button
              onClick={handleDeleteAccount}
              disabled={loading || deleteConfirm !== 'DELETE'}
              className="bg-red-900/60 hover:bg-red-800 disabled:opacity-40 text-red-300 text-xs px-4 py-2 rounded transition-colors"
            >
              Delete My Account
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
